import React, { useState, useEffect, useMemo } from 'react';
import { Linking } from 'react-native';
import {
  Box,
  VStack,
  HStack,
  Text,
  Heading,
  Button,
  ButtonText,
  Spinner,
  Pressable,
  Icon,
  Alert,
  AlertText,
  AlertIcon,
  Center,
} from '@gluestack-ui/themed';
import { useTranslation } from 'react-i18next';

import {
  formatEnglishDateShort,
  formatEnglishTime,
} from '@/common/utils/formatEnglishDateTime';
import {
  Calendar, Video, Clock, CheckCircle, XCircle, AlertCircle,
} from 'lucide-react-native';
import dayjs from 'dayjs';
import ExamPreflightChecklist from '@/student/module/components/task-types/event/components/ExamPreflightChecklist';

import { useNavigation } from '@react-navigation/native';
import type { NativeStackNavigationProp } from '@react-navigation/native-stack';

import { isLiveKitSlot } from '@/common/utils/oralExam/liveKitSlot';
import { ORAL_EXAM_PRACTICE_KIND_ID } from '@/common/utils/oralExam/practiceApi';
import type { StudentRouteParamList } from '@/common/navigation/RoleNavigations/Student/types';
import { resolveEventKindRequiresRecording } from '@/common/utils/taskEvaluationUtils';
import { OralExamPracticeEntry } from '@/student/course/components/OralExamPracticeEntry';
import { OralExamRegistrationCompleted } from '@/student/module/components/task-types/event/components/OralExamRegistrationCompleted';
import { OralExamRegistrationCancelled } from './task-types/event/components/OralExamRegistrationCancelled';
import { useEventTaskSlots } from '../hooks/useEventTaskSlots';
import { useDialog } from '../../../common/context/DialogContext';
import { useTenantEventKinds, useTenantSettingsStore } from '../../../common/context/TenantSettingsStore';
import { useUpdateSubmission } from '../../../common/hooks/student/task/useUpdateSubmission';
import { Module, Task } from '../../types/index';
import { AppointmentSlot } from '../../../common/api/models/appointmentSlot';

interface EventBookingSectionProps {
  task: Task;
  module: Module;
  selectedModule: { courseId: string; moduleId: string };
  readOnly?: boolean;
  onStartQuiz?: () => void;
}

/**
 * EventBookingSection
 * -------------------
 * Handles event task booking flow and session management.
 *
 * Business Logic Interpretation of State:
 * For events, the 'marked_as_done' state indicates that the user no longer
 * wishes to participate in further sessions (either because they finished
 * or because they decided to skip). The 'draft' state means we should still
 * show session availability and booking options.
 */
export const EventBookingSection: React.FC<EventBookingSectionProps> = ({
  task,
  module,
  selectedModule,
  readOnly = false,
  onStartQuiz,
}) => {
  const { t } = useTranslation();
  const navigation = useNavigation<NativeStackNavigationProp<StudentRouteParamList>>();
  const { showToastMessage, showDialog } = useDialog();
  const supportEmail = useTenantSettingsStore((s) => s.settings?.contact?.supportEmail);
  const { getEventKindById } = useTenantEventKinds();

  // IDs
  const courseId = selectedModule?.courseId || module?.courses?.[0]?.id;
  const moduleId = module?.id;
  const moduleVersion = module?.version;

  // Task states
  const isTaskLocked = task?.eventConfig?.locked;
  const isTaskCompleted = task?.submission?.state === 'marked_as_done';
  const completionPolicy = task?.evaluation?.completionPolicyEffective;
  const eventKindId = task?.eventConfig?.eventKindId;
  const isManualReleaseEvent = Boolean(eventKindId && getEventKindById(eventKindId)?.requiresManualRelease);
  // Oral exams are a single, graded exam session: they must never offer the generic
  // event-task escape hatches ("register for another session" / "mark as done").
  // Those belong to optional events like group study sessions.
  const isOralExam = task?.type === 'oral_exam';
  const isPracticeOralExam = eventKindId === ORAL_EXAM_PRACTICE_KIND_ID;

  // Local state
  const [selectedSlotId, setSelectedSlotId] = useState<string | null>(null);
  const [shouldPollBookings, setShouldPollBookings] = useState(false);
  const [isBookingAgain, setIsBookingAgain] = useState(false);
  const [fastExamPoll, setFastExamPoll] = useState(false);

  // Booking hook
  const {
    slots,
    slotsLoading,
    slotsError,
    currentBooking,
    cancelledBooking,
    currentSlot,
    liveKitJoin,
    meetingUrl,
    examSessionStatus,
    bookSlot,
    cancelBooking,
    bookingPending,
  } = useEventTaskSlots(courseId, moduleId, moduleVersion, task?.id, {
    enabled: !readOnly && !isTaskLocked && !isTaskCompleted && !isPracticeOralExam,
    pollSlots: true,
    pollBookings: !readOnly && shouldPollBookings,
    examSessionPollMs: fastExamPoll ? 3_000 : undefined,
    task,
  });

  const updateSubmission = useUpdateSubmission({ courseId, moduleId });

  // Derived states
  const isSessionCompleted = currentBooking?.status === 'completed';
  const hasActiveBooking = !!currentBooking;
  const isLiveKitOralExam = Boolean(liveKitJoin && isLiveKitSlot(currentSlot));
  const isBookingAdmitted = !!currentBooking?.examAdmission?.admitted;
  const oralExamJoinState = useMemo(() => {
    if (!currentBooking || !isLiveKitOralExam) return null;
    const rawState = currentBooking.oralExamJoinState ?? null;
    if (currentBooking.oralExamAdmitted && rawState !== 'over') {
      return 'open';
    }
    return rawState ?? 'open';
  }, [currentBooking, isLiveKitOralExam]);
  const canJoinOralExamLobby = oralExamJoinState === 'open';
  const oralExamJoinOpensAt = currentBooking && isLiveKitOralExam
    ? dayjs(currentBooking.startTime).subtract(15, 'minute')
    : null;
  const canCancelBooking = currentBooking && dayjs(currentBooking.startTime).diff(dayjs(), 'hours') >= 24;
  const isExamOpen = !!examSessionStatus?.openedAt && !examSessionStatus?.closedAt;
  const isExamClosed = !!examSessionStatus?.closedAt;
  const isPreflightWindow = useMemo(() => {
    if (!currentBooking || !isManualReleaseEvent) return false;
    const now = dayjs();
    const start = dayjs(currentBooking.startTime);
    const end = dayjs(currentBooking.endTime);
    return now.isAfter(start.subtract(10, 'minute')) && now.isBefore(end.add(10, 'minute'));
  }, [currentBooking, isManualReleaseEvent]);
  const showMarkDoneLink = completionPolicy === 'free'
    && !isTaskLocked
    && !isTaskCompleted
    && !isManualReleaseEvent
    && !isOralExam;

  // Effects
  useEffect(() => {
    const next = !!currentBooking;
    if (next !== shouldPollBookings) setShouldPollBookings(next);
  }, [currentBooking, shouldPollBookings]);

  useEffect(() => {
    if (isPreflightWindow !== fastExamPoll) setFastExamPoll(isPreflightWindow);
  }, [isPreflightWindow, fastExamPoll]);

  useEffect(() => {
    if (currentBooking?.status !== 'completed') {
      setIsBookingAgain(false);
    }
  }, [currentBooking]);

  const formatDate = (d: string) => formatEnglishDateShort(d);
  const formatTime = (d: string) => formatEnglishTime(d);

  // Next session info for countdown
  const nextSessionInfo = useMemo(() => {
    if (!slots || slots.length === 0) return null;
    const now = new Date();
    const futureSlots = slots
      .filter((s) => new Date(s.startTime) > now)
      .sort((a, b) => new Date(a.startTime).getTime() - new Date(b.startTime).getTime());
    if (futureSlots.length === 0) return null;

    const next = futureSlots[0];
    const diffMs = new Date(next.startTime).getTime() - now.getTime();
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

    return {
      slot: next,
      daysUntil: diffDays,
      isToday: diffDays === 0,
      isTomorrow: diffDays === 1,
    };
  }, [slots]);

  // Handlers
  const handleBook = async () => {
    if (readOnly || !selectedSlotId) return;
    const selectedSlot = slots.find((s) => s.slotId === selectedSlotId);
    if (!selectedSlot) return;

    const eventKindId = task?.eventConfig?.eventKindId;
    const eventKind = eventKindId ? getEventKindById(eventKindId) : undefined;
    const isRecordingRequired = eventKind ? resolveEventKindRequiresRecording(eventKind) : false;

    showDialog({
      title: t('student.eventTask.registrationConfirmationTitle'),
      message: (() => {
        const base = t('student.eventTask.registrationConfirmationBase', {
          date: formatDate(selectedSlot.startTime),
          time: formatTime(selectedSlot.startTime),
        });
        if (!isRecordingRequired) return base;
        const notice = task?.eventConfig?.recordingConsentText || t('student.eventTask.registrationRecordingNotice');
        return `${base}\n\n${notice}`;
      })(),
      buttons: [
        { text: t('common.buttons.cancel'), variant: 'outline', action: 'secondary' },
        {
          text: t('student.eventTask.confirmRegistration'),
          action: 'positive',
          onPress: async () => {
            try {
              const recordingConsentGiven = isRecordingRequired ? true : undefined;
              await bookSlot(selectedSlotId, recordingConsentGiven);
              setShouldPollBookings(true);
              showToastMessage('success', t('student.eventTask.registrationSuccess'));
            } catch {
              showToastMessage('error', t('student.eventTask.registrationError'));
            }
          },
        },
      ],
    });
  };

  const handleCancel = () => {
    if (readOnly || !currentBooking) return;
    showDialog({
      title: t('student.eventTask.cancelConfirmationTitle'),
      message: t('student.eventTask.cancelConfirmation', {
        date: formatDate(currentBooking.startTime),
        time: formatTime(currentBooking.startTime),
      }),
      buttons: [
        { text: t('student.eventTask.abort'), variant: 'outline', action: 'secondary' },
        {
          text: t('student.eventTask.cancelButton'),
          action: 'negative',
          onPress: async () => {
            try {
              await cancelBooking(currentBooking.bookingId);
              showToastMessage('success', t('student.eventTask.cancelSuccess'));
            } catch {
              showToastMessage('error', t('student.eventTask.cancelError'));
            }
          },
        },
      ],
    });
  };

  const handleMarkDone = () => {
    if (readOnly) return;
    showDialog({
      title: t('student.eventTask.markDoneConfirmTitle', 'Mark as done?'),
      message: t('student.eventTask.markDoneConfirmMessage', 'You can undo this later.'),
      buttons: [
        { text: t('common.buttons.cancel'), variant: 'outline', action: 'secondary' },
        {
          text: t('student.eventTask.markDoneLink', 'Mark as done'),
          action: 'positive',
          onPress: async () => {
            try {
              await updateSubmission.mutateAsync({
                courseId,
                moduleId,
                taskId: task.id,
                data: { text: task?.submission?.text || '', state: 'marked_as_done' },
              });
              showToastMessage('success', t('student.eventTask.markDoneSuccess'));
            } catch {
              showToastMessage('error', t('student.eventTask.markDoneError'));
            }
          },
        },
      ],
    });
  };

  const handleUnmarkDone = async () => {
    if (readOnly) return;
    try {
      await updateSubmission.mutateAsync({
        courseId,
        moduleId,
        taskId: task.id,
        data: { text: task?.submission?.text || '', state: 'draft' },
      });
      setIsBookingAgain(true); // Automatically show slot selection after unmarking
      showToastMessage('success', t('student.eventTask.unmarkSuccess', 'You can register for another session now'));
    } catch (e) {
      showToastMessage('error', t('student.eventTask.unmarkError', 'Could not enable re-registration'));
    }
  };

  // LOCKED STATE — oral exams keep prep content visible; only registration is blocked
  if (isTaskLocked) {
    if (isOralExam) {
      return (
        // @ts-expect-error - flush is a custom variant
        <Alert action="warning" variant="flush" testID="oral-exam-registration-locked">
          <AlertIcon as={AlertCircle} mr="$2" />
          <Text size="sm">
            {t('student.eventTask.registrationLockedMessage')}
          </Text>
        </Alert>
      );
    }

    return (
      // @ts-expect-error - flush is a custom variant
      <Alert action="warning" variant="flush">
        <AlertIcon as={AlertCircle} mr="$2" />
        <Text size="sm">
          {t('student.eventTask.lockedMessage')}
        </Text>
      </Alert>
    );
  }

  if (isPracticeOralExam) {
    return <OralExamPracticeEntry taskId={task?.id} />;
  }

  if (isOralExam && (isTaskCompleted || isSessionCompleted)) {
    return <OralExamRegistrationCompleted attendedAtIso={currentBooking?.startTime} />;
  }

  // COMPLETED STATE
  if (isTaskCompleted) {
    return (
      <VStack space="md" alignItems="center">
        {/* @ts-expect-error - flush is a custom variant */}
        <Alert action="success" variant="flush">
          <AlertIcon as={CheckCircle} mr="$2" />
          <AlertText size="sm" fontWeight="bold">
            {t('student.eventTask.completedInstructions', 'Session completed')}
          </AlertText>
        </Alert>
        {!readOnly && !isOralExam && (
          <Button
            variant="outline"
            size="sm"
            action="secondary"
            onPress={handleUnmarkDone}
            isDisabled={updateSubmission.isPending}
          >
            {updateSubmission.isPending ? (
              <HStack space="xs" alignItems="center">
                <Spinner size="small" />
                <ButtonText size="xs">Processing...</ButtonText>
              </HStack>
            ) : (
              <ButtonText size="xs">
                {t('student.eventTask.registerAnother', 'Register for Another Session')}
              </ButtonText>
            )}
          </Button>
        )}
      </VStack>
    );
  }

  // LOADING STATE
  if (slotsLoading) {
    return (
      <VStack p="$4" alignItems="center" space="sm">
        <Spinner size="small" />
        <Text size="xs" color="$textSubtle">
          Loading available sessions...
        </Text>
      </VStack>
    );
  }

  // ERROR STATE
  if (slotsError) {
    return (
      // @ts-expect-error - flush is a custom variant
      <Alert action="error" variant="flush">
        <AlertIcon as={XCircle} mr="$2" />
        <AlertText size="sm">{t('student.eventTask.loadError', 'Could not load sessions.')}</AlertText>
      </Alert>
    );
  }

  // ACTIVE BOOKING - Show booking details
  if (hasActiveBooking && !isBookingAgain) {
    const isConfirmed = currentBooking.status === 'confirmed' || currentBooking.status === 'pending';

    return (
      <VStack space="md">
        {/* Booking Card */}
        {/* @ts-expect-error - accent-flush is a custom variant */}
        <Alert action="info" variant="accent-flush">
          <VStack space="md" flex={1}>
            <HStack space="sm" alignItems="center">
              <AlertIcon as={Calendar} />
              <AlertText size="xs" fontWeight="bold" textTransform="uppercase">
                {isSessionCompleted ? 'Session Completed' : 'Booked'}
              </AlertText>
            </HStack>
            <Text size="md" fontWeight="bold" color="$textHeading">
              {formatDate(currentBooking.startTime)}
              {' '}
              at
              {formatTime(currentBooking.startTime)}
            </Text>

            {/* LiveKit oral exam: join window messaging + lobby entry */}
            {!!(isConfirmed && isLiveKitOralExam && liveKitJoin && oralExamJoinState === 'upcoming') && (
              <VStack space="xs" pt="$1">
                <HStack alignItems="center" space="sm">
                  <Icon as={Clock} size="xs" color="$textSubtle" />
                  <Text size="sm" color="$textSubtle">
                    {t('oralExam.booking.joinOpensAt', {
                      time: oralExamJoinOpensAt ? formatTime(oralExamJoinOpensAt.toISOString()) : '',
                    })}
                  </Text>
                </HStack>
              </VStack>
            )}

            {!!(isConfirmed && isLiveKitOralExam && liveKitJoin && oralExamJoinState === 'in_progress') && (
              <VStack space="xs" pt="$1">
                <HStack alignItems="center" space="sm">
                  <Icon as={AlertCircle} size="xs" color="$statusWarning" />
                  <Text size="sm" color="$statusWarning" fontWeight="semibold">
                    {t('oralExam.booking.examInProgress', {
                      defaultValue: 'This session is currently in progress',
                    })}
                  </Text>
                </HStack>
                <Text size="xs" color="$textSubtle">
                  {t('oralExam.booking.examInProgressHint', {
                    defaultValue: 'The join window has closed. You do not need to contact anyone. Non-attendance will be processed after the session ends, and then you can register for another session.',
                  })}
                </Text>
              </VStack>
            )}

            {!!(isConfirmed && isLiveKitOralExam && liveKitJoin && oralExamJoinState === 'over') && (
              <VStack space="xs" pt="$1">
                <HStack alignItems="center" space="sm">
                  <Icon as={AlertCircle} size="xs" color="$statusWarning" />
                  <Text size="sm" color="$statusWarning" fontWeight="semibold">
                    {t('oralExam.booking.examMissed')}
                  </Text>
                </HStack>
                <Text size="xs" color="$textSubtle">
                  {t('oralExam.booking.examMissedHint')}
                </Text>
              </VStack>
            )}

            {!!(isConfirmed && isLiveKitOralExam && liveKitJoin && canJoinOralExamLobby) && (
              <Button
                size="sm"
                variant="solid"
                bg="$brandBase"
                onPress={() => {
                  if (!currentBooking?.slotId) return;
                  navigation.navigate('SlotMeetingScreen', { slotId: currentBooking.slotId });
                }}
                testID="join-oral-exam-room"
              >
                <HStack space="xs" alignItems="center">
                  <Icon as={Video} size="xs" color="white" />
                  <ButtonText size="xs">{t('oralExam.joinLobby')}</ButtonText>
                </HStack>
              </Button>
            )}

            {/* Teams / external meeting URL for non-LiveKit bookings */}
            {!!(isConfirmed && meetingUrl && !isLiveKitOralExam) && (
              <Button
                size="sm"
                variant="solid"
                bg="$brandBase"
                onPress={() => {
                  // Same as EventBookingDetails: Linking.openURL, not window.open
                  // (window.open is undefined on RN → FLARE-39 / FLARE-7T).
                  Linking.openURL(meetingUrl).catch(() => {});
                }}
              >
                <HStack space="xs" alignItems="center">
                  <Icon as={Video} size="xs" color="white" />
                  <ButtonText size="xs">{t('student.eventTask.joinMeeting', 'Join Meeting')}</ButtonText>
                </HStack>
              </Button>
            )}

            {/* Proctored exam preflight state */}
            {!!(isManualReleaseEvent && isConfirmed && isPreflightWindow) && (
              <ExamPreflightChecklist
                meetingUrl={meetingUrl}
                isBookingAdmitted={isBookingAdmitted}
                isExamOpen={isExamOpen}
                isExamClosed={isExamClosed}
                onStartQuiz={onStartQuiz}
              />
            )}

            {/* Cancel option */}
            {!!canCancelBooking && (
              <Button size="sm" variant="link" onPress={handleCancel} alignSelf="flex-start">
                <ButtonText size="xs" color="$statusError">
                  Cancel Booking
                </ButtonText>
              </Button>
            )}
          </VStack>
        </Alert>

        {/* Book again option after session completed */}
        {!!(isSessionCompleted && !isOralExam) && (
          <Button size="sm" variant="outline" onPress={() => setIsBookingAgain(true)}>
            <ButtonText size="xs">Register for Another Session</ButtonText>
          </Button>
        )}

        {/* Mark as done */}
        {!!(showMarkDoneLink && isSessionCompleted) && (
          <Button size="sm" bg="$brandBase" onPress={handleMarkDone}>
            <ButtonText size="xs" color="white" fontWeight="bold">
              Mark Task as Done
            </ButtonText>
          </Button>
        )}
      </VStack>
    );
  }

  // SLOT SELECTION - Show available slots
  return (
    <VStack space="lg">
      {isOralExam && cancelledBooking && (
        <OralExamRegistrationCancelled startIso={cancelledBooking.startTime} />
      )}
      {/* Countdown badge */}
      {!!nextSessionInfo && (
        <HStack space="sm" alignItems="center">
          <Icon as={Clock} size="xs" color="$brandBase" />
          <Text size="xs" fontWeight="bold" color="$brandBase">
            {nextSessionInfo.isToday
              ? 'Session available today!'
              : nextSessionInfo.isTomorrow
                ? 'Next session tomorrow'
                : `Next session in ${nextSessionInfo.daysUntil} days`}
          </Text>
        </HStack>
      )}

      {/* Slots list */}
      {slots.length === 0 ? (
        <Center py="$8">
          <VStack alignItems="center" space="md">
            <Icon as={Calendar} size="xl" color="$textHint" />
            <Text size="lg" color="$textSubtle" textAlign="center">
              {t('student.eventTask.noSlotsAvailable', 'No sessions currently available.')}
            </Text>
            {!!supportEmail && (
              <Text size="sm" color="$textHint" textAlign="center">
                {t('student.eventTask.contactSupport', 'Contact {{email}} for assistance.', { email: supportEmail })}
              </Text>
            )}
          </VStack>
        </Center>
      ) : (
        <VStack space="sm">
          {isOralExam ? (
            <Text size="sm" color="$textSubtle">{t('student.eventTask.chooseSession')}</Text>
          ) : (
            <Heading size="sm">{t('student.eventTask.chooseSession')}</Heading>
          )}
          {slots.slice(0, 4).map((slot: AppointmentSlot) => (
            <Pressable key={slot.slotId} onPress={() => setSelectedSlotId(slot.slotId)}>
              {({ pressed }) => (
                <Box
                  p="$3"
                  borderRadius="$lg"
                  borderWidth={2}
                  borderColor={selectedSlotId === slot.slotId ? '$borderBrand' : '$borderLight'}
                  bg={selectedSlotId === slot.slotId ? '$bgBrandSubtle' : '$bgApp'}
                  opacity={pressed ? 0.8 : 1}
                >
                  <HStack justifyContent="space-between" alignItems="center">
                    <VStack>
                      <Text size="sm" fontWeight="bold" color="$textHeading">
                        {formatDate(slot.startTime)}
                      </Text>
                      <Text size="xs" color="$textSubtle">
                        {formatTime(slot.startTime)}
                        {' '}
                        -
                        {formatTime(slot.endTime)}
                      </Text>
                    </VStack>
                    {!!(slot.capacity > 1) && (
                      <Text size="xs" color="$textSubtle">
                        {slot.availableSpots}
                        {' '}
                        spots
                      </Text>
                    )}
                  </HStack>
                </Box>
              )}
            </Pressable>
          ))}
        </VStack>
      )}

      {/* Book button */}
      {!!selectedSlotId && (
        <Button size="md" bg="$brandBase" onPress={handleBook} isDisabled={bookingPending}>
          <ButtonText>{bookingPending ? 'Booking...' : 'Book Session'}</ButtonText>
        </Button>
      )}

      {/* Mark as done without booking (if allowed) */}
      {!!showMarkDoneLink && (
        <Button size="sm" variant="link" onPress={handleMarkDone} alignSelf="center">
          <ButtonText size="xs" color="$textSubtle">
            {t('student.eventTask.markDoneLink', 'Skip event, mark as done')}
          </ButtonText>
        </Button>
      )}

      {/* Cancel booking again */}
      {!!isBookingAgain && (
        <Button size="sm" variant="link" onPress={() => setIsBookingAgain(false)}>
          <ButtonText size="xs" color="$textSubtle">
            Cancel
          </ButtonText>
        </Button>
      )}
    </VStack>
  );
};

export default EventBookingSection;
